import { useEffect, useMemo, useRef, useState } from "react";
import Fuse from "fuse.js";
import { Link } from "wouter";
import { publicCalculators, categories, WORKING_CALCULATOR_COUNT_LABEL } from "@/data/calculators";
import { withTrailingSlash } from "@/lib/urls";

const allItems = publicCalculators.map((c) => ({
  slug: c.slug,
  title: c.title,
  description: c.description,
  category: c.categoryName,
  categorySlug: c.categorySlug,
  keywords: c.keywords,
  isNew: c.isNew ?? false,
  isTrending: c.isTrending ?? false,
}));

const fuse = new Fuse(allItems, {
  keys: [
    { name: "title", weight: 2 },
    { name: "keywords", weight: 1.5 },
    { name: "category", weight: 1 },
    { name: "description", weight: 0.8 },
  ],
  threshold: 0.35,
  includeScore: true,
  minMatchCharLength: 2,
});

const TRENDING_SLUGS = [
  "mortgage-payment-calculator",
  "bmi-calculator",
  "percentage-calculator",
  "compound-interest-calculator",
  "calorie-calculator",
];

interface Props {
  open: boolean;
  onClose: () => void;
}

export function CommandPalette({ open, onClose }: Props) {
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIndex(0);
      setTimeout(() => inputRef.current?.focus(), 40);
    }
  }, [open]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        onClose();
      }
      if (!open) return;
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowDown") { e.preventDefault(); setActiveIndex((i) => i + 1); }
      if (e.key === "ArrowUp") { e.preventDefault(); setActiveIndex((i) => Math.max(0, i - 1)); }
      if (e.key === "Enter") {
        const link = listRef.current?.querySelectorAll("a")[activeIndex];
        if (link) { (link as HTMLAnchorElement).click(); onClose(); }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, activeIndex, onClose]);

  const results = useMemo(() => {
    if (!query.trim()) {
      return TRENDING_SLUGS.map((slug) => allItems.find((c) => c.slug === slug)).filter(Boolean) as typeof allItems;
    }
    return fuse.search(query).slice(0, 12).map((r) => r.item);
  }, [query]);

  const grouped = useMemo(() => {
    const map = new Map<string, typeof allItems>();
    for (const item of results) {
      const cat = item.category;
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(item);
    }
    return map;
  }, [results]);

  if (!open) return null;

  return (
    <div className="cp-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label="Search calculators">
      <div className="cp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cp-search-row">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
          <input
            ref={inputRef}
            className="cp-input"
            placeholder={`Search ${WORKING_CALCULATOR_COUNT_LABEL} calculators…`}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActiveIndex(0); }}
            autoComplete="off"
            spellCheck={false}
          />
          <kbd className="cp-esc" onClick={onClose}>Esc</kbd>
        </div>

        <div className="cp-label">{query.trim() ? `${results.length} results` : "Trending"}</div>

        <ul className="cp-list" ref={listRef} role="listbox">
          {query.trim() ? (
            results.map((item, i) => (
              <li key={item.slug} role="option" aria-selected={i === activeIndex}>
                <Link href={withTrailingSlash(`/calculator/${item.slug}`)} className={`cp-item${i === activeIndex ? " active" : ""}`} onClick={onClose}>
                  <span className="cp-item-title">
                    {item.title}
                    {item.isTrending && <span className="cp-badge trending">Trending</span>}
                    {item.isNew && <span className="cp-badge new">New</span>}
                  </span>
                  <span className="cp-item-meta">{item.category}</span>
                </Link>
              </li>
            ))
          ) : (
            Array.from(grouped.entries()).map(([cat, items]) => (
              <li key={cat} className="cp-group">
                <div className="cp-group-label">{cat}</div>
                {items.map((item, i) => (
                  <Link key={item.slug} href={withTrailingSlash(`/calculator/${item.slug}`)} className="cp-item" onClick={onClose}>
                    <span className="cp-item-title">
                      {item.title}
                      {item.isTrending && <span className="cp-badge trending">Trending</span>}
                      {item.isNew && <span className="cp-badge new">New</span>}
                    </span>
                  </Link>
                ))}
              </li>
            ))
          )}
          {results.length === 0 && (
            <li className="cp-empty">No calculators found for "{query}"</li>
          )}
        </ul>

        <div className="cp-footer">
          <span><kbd>↑↓</kbd> navigate</span>
          <span><kbd>Enter</kbd> open</span>
          <span><kbd>Esc</kbd> close</span>
          <span><kbd>⌘K</kbd> toggle</span>
        </div>
      </div>
    </div>
  );
}
